/**
 * Wishlist Page Functionality
 * Handles the wishlist page UI including loading products, sharing, and bulk actions
 */
(function() {
  'use strict';

  class WishlistPage {
    constructor() {
      this.container = document.getElementById('wishlist-products');
      this.emptyState = document.getElementById('wishlist-empty');
      this.countText = document.getElementById('wishlist-count-text');
      this.addAllButton = document.getElementById('add-all-to-cart');
      this.shareButton = document.getElementById('share-wishlist');
      this.shareModal = document.getElementById('share-modal');
      this.shareUrlInput = document.getElementById('share-url-input');
      this.copyLinkButton = document.getElementById('copy-share-link');
      this.loginBanner = document.getElementById('login-to-save-banner');
      
      if (!this.container) {
        return;
      }

      this.loadGeneration = 0;
      this.init();
      this.attachEventListeners();
    }

    init() {
      if (!window.WishlistManager) {
        setTimeout(() => this.init(), 100);
        return;
      }
      
      this.loadSharedWishlist();
      this.loadProducts();
    }
    
    loadSharedWishlist() {
      const urlParams = new URLSearchParams(window.location.search);
      const sharedData = urlParams.get('shared');
      
      if (sharedData) {
        // Import a given share link only once per browser. A bookmarked
        // share link would otherwise re-add every item on each visit and
        // undo everything the customer removed since.
        const importedKey = 'wishlist:shared-imported';
        let imported = [];
        try {
          imported = JSON.parse(localStorage.getItem(importedKey)) || [];
        } catch (e) {
          imported = [];
        }
        if (imported.includes(sharedData)) {
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }

        try {
          const items = decodeURIComponent(sharedData).split(',').map(item => {
            const parts = item.split(':');
            return {
              handle: parts[0],
              variantId: parts[1] ? parseInt(parts[1]) : null
            };
          });
          
          items.forEach(item => {
            if (!window.WishlistManager.hasItem(item.handle)) {
              window.WishlistManager.addItem(item.handle, item.variantId);
            }
          });
          
          localStorage.setItem(importedKey, JSON.stringify(imported.concat(sharedData).slice(-20)));

          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (e) {
          console.error('Error loading shared wishlist:', e);
        }
      }
    }

    attachEventListeners() {
      document.addEventListener('wishlist:updated', () => this.loadProducts());
      
      if (this.addAllButton) {
        this.addAllButton.addEventListener('click', () => this.addAllToCart());
      }
      
      if (this.shareButton) {
        this.shareButton.addEventListener('click', () => this.openShareModal());
      }
      
      if (this.copyLinkButton) {
        this.copyLinkButton.addEventListener('click', () => this.copyShareLink());
      }
      
      // Share modal event listeners
      if (this.shareModal) {
        // Close button
        const closeBtn = this.shareModal.querySelector('.wishlist-share-modal__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => this.closeShareModal());
        }
        
        // Click on overlay to close
        const overlay = this.shareModal.querySelector('.wishlist-share-modal__overlay');
        if (overlay) {
          overlay.addEventListener('click', () => this.closeShareModal());
        }
        
        // Social media platform buttons
        const platformButtons = this.shareModal.querySelectorAll('.wishlist-share-platform');
        platformButtons.forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const platform = btn.dataset.platform;
            this.shareViaPlatform(platform);
          });
        });
      }
      
      // Login banner close
      if (this.loginBanner) {
        const closeBtn = this.loginBanner.querySelector('.wishlist-login-banner__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            this.loginBanner.style.display = 'none';
            sessionStorage.setItem('wishlist-login-banner-closed', 'true');
          });
        }
        
        // Show banner if not logged in and not previously closed
        if (!window.Shopify?.customer && !sessionStorage.getItem('wishlist-login-banner-closed')) {
          const wishlist = window.WishlistManager?.getWishlist() || [];
          if (wishlist.length > 0) {
            this.loginBanner.style.display = 'flex';
          }
        }
      }

      // Handle remove buttons via event delegation
      this.container.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.wishlist-card__remove');
        if (removeBtn) {
          e.preventDefault();
          const handle = removeBtn.dataset.productHandle;
          if (handle && window.WishlistManager) {
            window.WishlistManager.removeItem(handle);
          }
        }
      });
      
      // Handle add-to-cart buttons via event delegation
      this.container.addEventListener('click', (e) => {
        const atcButton = e.target.closest('.product-card__atc');
        if (atcButton) {
          e.preventDefault();
          e.stopPropagation();
          this.addSingleToCart(atcButton);
        }
      });
      
      // Prevent form submission (forms are handled via AJAX)
      this.container.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-type="add-to-cart-form"]');
        if (form) {
          e.preventDefault();
          const atcButton = form.querySelector('.product-card__atc');
          if (atcButton) {
            this.addSingleToCart(atcButton);
          }
        }
      });
    }
    
    async addSingleToCart(button) {
      // Find the form and variant ID
      const form = button.closest('form');
      if (!form) return;
      
      const variantInput = form.querySelector('input[name="id"]');
      if (!variantInput) return;
      
      const variantId = variantInput.value;
      if (!variantId) return;
      
      // Disable button and show loading state
      button.disabled = true;
      const originalHTML = button.innerHTML;
      button.innerHTML = `
        <span class="product-card__atc-icon product-card__action-icon inline-flex">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="animate-spin">
            <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
            <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
          </svg>
        </span>
        <span class="product-card__atc-text product-card__action-text">Toevoegen...</span>
      `;
      
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId), quantity: 1 }]
          })
        });
        
        if (response.ok) {
          // Success state
          button.innerHTML = `
            <span class="product-card__atc-icon product-card__action-icon inline-flex">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </span>
            <span class="product-card__atc-text product-card__action-text">Toegevoegd!</span>
          `;
          
          // Update cart count via FoxTheme pubsub (if available)
          try {
            if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
              const cartResponse = await fetch('/cart.js');
              const cart = await cartResponse.json();
              
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                cart: cart
              });
            }
          } catch (pubsubError) {
            console.log('Pubsub update skipped:', pubsubError);
          }
          
          // Trigger cart refresh event
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
          
          // Reset button after delay
          setTimeout(() => {
            button.innerHTML = originalHTML;
            button.disabled = false;
          }, 2000);
        } else {
          // Show error but don't alert
          console.error('Failed to add to cart:', response.status);
          button.innerHTML = originalHTML;
          button.disabled = false;
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        button.innerHTML = originalHTML;
        button.disabled = false;
      }
    }

    async loadProducts() {
      // Generation token: successive wishlist:updated events can overlap;
      // only the newest run may touch the DOM, or a slower stale run would
      // re-render items the user just removed.
      const generation = ++this.loadGeneration;

      const wishlist = window.WishlistManager.getWishlist();

      this.updateCount(wishlist.length);

      if (wishlist.length === 0) {
        this.showEmptyState();
        return;
      }

      this.hideEmptyState();
      this.container.innerHTML = '<div class="wishlist-page__loading">Laden...</div>';

      const productCards = await Promise.all(
        wishlist.map(item => this.loadProductCard(item))
      );

      if (generation !== this.loadGeneration) {
        return; // superseded by a newer render
      }

      const fragments = productCards.filter(Boolean);

      if (fragments.length > 0) {
        this.container.innerHTML = '';
        for (const fragment of fragments) {
          this.container.appendChild(fragment);
        }
      } else {
        this.showEmptyState();
      }
    }

    async loadProductCard(item) {
      try {
        let response = await fetch(`/products/${encodeURIComponent(item.handle)}?view=wishlist-card`);

        if (response.ok) {
          let effectiveHandle = item.handle;

          // The product's handle changed since it was stored (Shopify 301'd
          // the old one). Self-heal the stored entry so future matching works.
          if (response.redirected) {
            const match = new URL(response.url).pathname.match(/\/products\/([^\/?]+)/);
            if (!match) {
              // Redirected to a non-product URL: treat as unavailable.
              return this.buildUnavailableCard(item.handle);
            }
            const newHandle = decodeURIComponent(match[1]);
            if (newHandle !== item.handle) {
              window.WishlistManager.renameItem(item.handle, newHandle, { silent: true });
              effectiveHandle = newHandle;
              // Re-fetch under the new handle: the redirect may have dropped
              // the ?view= param, which would return the full product page.
              response = await fetch(`/products/${encodeURIComponent(newHandle)}?view=wishlist-card`);
              if (!response.ok) {
                return this.buildUnavailableCard(newHandle);
              }
            }
          }

          const html = await response.text();
          const template = document.createElement('template');
          template.innerHTML = html.trim();

          // The snippet stamps the product's current handle; override it with
          // the handle the wishlist actually stores so removal always matches.
          template.content.querySelectorAll('.wishlist-card__remove').forEach(btn => {
            btn.dataset.productHandle = effectiveHandle;
          });

          return template.content;
        }

        if (response.status === 404) {
          // Deleted or draft product. Keep the item (drafts can come back)
          // but show a fallback card so the customer can still remove it.
          return this.buildUnavailableCard(item.handle);
        }

        return null;
      } catch (error) {
        console.error(`Error loading product ${item.handle}:`, error);
        return null;
      }
    }

    buildUnavailableCard(handle) {
      const template = document.createElement('template');
      template.innerHTML = `
        <div class="wishlist-card wishlist-card--unavailable relative">
          <button
            class="wishlist-card__remove"
            aria-label="Verwijderen uit wishlist"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
          <div class="wishlist-card__unavailable-body">
            <p class="wishlist-card__unavailable-text">Dit product is niet meer beschikbaar</p>
          </div>
        </div>
      `;
      // Set the handle via dataset (not string interpolation) to avoid injection.
      template.content.querySelector('.wishlist-card__remove').dataset.productHandle = handle;
      return template.content;
    }

    updateCount(count) {
      if (this.countText) {
        this.countText.textContent = `${count} product${count !== 1 ? 'en' : ''}`;
      }
      
      if (this.addAllButton) {
        this.addAllButton.style.display = count > 0 ? 'inline-flex' : 'none';
      }
      
      if (this.shareButton) {
        this.shareButton.style.display = count > 0 ? 'inline-flex' : 'none';
      }
    }

    showEmptyState() {
      if (this.emptyState) {
        this.emptyState.style.display = 'block';
      }
      this.container.innerHTML = '';
    }

    hideEmptyState() {
      if (this.emptyState) {
        this.emptyState.style.display = 'none';
      }
    }

    async addAllToCart() {
      const wishlist = window.WishlistManager.getWishlist();
      
      if (wishlist.length === 0) return;
      
      this.addAllButton.disabled = true;
      const originalText = this.addAllButton.textContent;
      this.addAllButton.textContent = 'Toevoegen...';
      
      const items = [];
      
      for (const item of wishlist) {
        try {
          const response = await fetch(`/products/${item.handle}.js`);
          if (response.ok) {
            const product = await response.json();
            const variantId = item.variantId || product.variants[0].id;
            items.push({ id: variantId, quantity: 1 });
          }
        } catch (error) {
          console.error(`Error getting product ${item.handle}:`, error);
        }
      }
      
      if (items.length > 0) {
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items })
          });
          
          if (response.ok) {
            // Update cart count via FoxTheme pubsub (if available)
            if (window.FoxTheme && window.FoxTheme.pubsub && window.FoxTheme.pubsub.publish) {
              const cartResponse = await fetch('/cart.js');
              const cart = await cartResponse.json();
              
              FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                cart: cart
              });
            }
            
            // Trigger cart refresh event and open drawer
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
          }
        } catch (error) {
          console.error('Error adding items to cart:', error);
        }
      }
      
      this.addAllButton.disabled = false;
      this.addAllButton.textContent = originalText;
    }

    openShareModal() {
      if (!window.WishlistManager || !this.shareModal) return;
      
      const wishlist = window.WishlistManager.getWishlist();
      
      if (wishlist.length === 0) {
        alert('Je wishlist is leeg');
        return;
      }
      
      // Genereer share URL
      const wishlistData = wishlist.map(item => 
        `${item.handle}${item.variantId ? ':' + item.variantId : ''}`
      ).join(',');
      
      this.currentShareUrl = `${window.location.origin}/pages/wishlist?shared=${encodeURIComponent(wishlistData)}`;
      
      // Vul input in
      if (this.shareUrlInput) {
        this.shareUrlInput.value = this.currentShareUrl;
      }
      
      // Toon modal
      this.shareModal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    closeShareModal() {
      if (!this.shareModal) return;
      
      this.shareModal.hidden = true;
      document.body.style.overflow = '';
    }
    
    shareViaPlatform(platform) {
      if (!this.currentShareUrl) return;
      
      const text = 'Bekijk mijn wishlist!';
      let shareUrl;
      
      switch(platform) {
        case 'whatsapp':
          shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + this.currentShareUrl)}`;
          break;
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.currentShareUrl)}`;
          break;
        case 'instagram':
          // Instagram heeft geen directe share URL, kopieer link naar clipboard en open Instagram
          navigator.clipboard.writeText(this.currentShareUrl).then(() => {
            alert('Link gekopieerd! Plak deze in je Instagram Story of bericht.');
            window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
          });
          return;
        case 'email':
          shareUrl = `mailto:?subject=${encodeURIComponent('Mijn wishlist')}&body=${encodeURIComponent(text + '\n\n' + this.currentShareUrl)}`;
          break;
      }
      
      if (shareUrl) {
        window.open(shareUrl, '_blank', 'noopener,noreferrer');
      }
    }

    async copyShareLink() {
      if (!this.shareUrlInput) return;
      
      try {
        await navigator.clipboard.writeText(this.shareUrlInput.value);
        
        const originalText = this.copyLinkButton.textContent;
        this.copyLinkButton.textContent = 'Gekopieerd!';
        
        setTimeout(() => {
          this.copyLinkButton.textContent = originalText;
        }, 2000);
      } catch (error) {
        // Fallback for older browsers
        this.shareUrlInput.select();
        document.execCommand('copy');
        
        const originalText = this.copyLinkButton.textContent;
        this.copyLinkButton.textContent = 'Gekopieerd!';
        
        setTimeout(() => {
          this.copyLinkButton.textContent = originalText;
        }, 2000);
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new WishlistPage());
  } else {
    new WishlistPage();
  }
})();
